import { useState } from 'react';

const team = [
  {
    name: 'Melinda Chere Melville Page',
    role: 'Director',
    email: 'melinda@casachere.com',
    image: '',
    impact: 'Director and strategic architect of the firm\'s multi-sector investment philosophy. With over 25 years in international finance, Page sets the long-term vision and chairs the investment committee. Her insistence on rigorous governance has been the defining force behind consistent performance across market cycles.',
  },
  {
    name: 'Isak Bernstein',
    role: 'Chief Executive Officer',
    email: 'isak@casachere.com',
    image: '',
    impact: 'Operational backbone of the firm with 20+ years in corporate structuring and UK regulatory frameworks. Bernstein personally oversees all legal and compliance operations, ensuring flawless regulatory standing across 18 countries and multiple jurisdictions.',
  },
  {
    name: 'Benjamin Schimon',
    role: 'Chief Financial Officer',
    email: 'benjamin@casachere.com',
    image: '',
    impact: 'Leads all financial strategy, treasury, and reporting. A former finance director at a top-tier UK investment house, Schimon brings two decades of multi-asset financial expertise. Under his stewardship, the portfolio has grown from €1.2B to €2.4B while maintaining a 12.8% average annual return.',
  },
  {
    name: 'Sarah Mayer',
    role: 'Chief Operating Officer',
    email: 'sarah@casachere.com',
    image: '',
    impact: 'Drives the operational machinery behind the firm\'s investment platform. Mayer oversees cross-sector execution, technology infrastructure, and process governance — ensuring that 47 active investments across five sectors run to consistent institutional standards. Her background spans operations leadership at global asset managers and large-scale transformation programs.',
  },
  {
    name: 'Ilanah Blum',
    role: 'Head of Investor Relations',
    email: 'ilanah@casachere.com',
    image: '',
    impact: 'The primary point of contact for all institutional and professional investors. Blum manages relationships with pension funds, family offices, and sovereign wealth funds across Europe and the Middle East. Her transparent communication style and deep product knowledge ensure that every investor understands their exposure and receives timely, accurate reporting.',
  },
  {
    name: 'Veronika Graff',
    role: 'Head of Portfolio Operations',
    email: 'veronika@casachere.com',
    image: '',
    impact: 'Oversees the day-to-day management of 47 active investments across five sectors. Graff\'s operational discipline ensures that every asset — from London commercial towers to Andalusian solar farms to Zug blockchain nodes — receives active monitoring, performance optimization, and rigorous reporting. Her team is the engine that keeps the portfolio running at peak efficiency.',
  },
  {
    name: 'Teodora Cohen',
    role: 'Head of Real Estate Investments',
    email: 'teodora@casachere.com',
    image: '',
    impact: 'Leads the firm\'s largest sector allocation at 35% of portfolio value. Cohen oversees €840M in premium commercial real estate assets across 12 countries — from London luxury offices to Singapore Grade-A towers to Berlin mixed-use developments. With 25 years in institutional real estate investment, she has sourced and structured every major property acquisition since joining the firm, delivering consistent double-digit returns through market cycles.',
  },
];

export default function ContactPage() {
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    organization: '',
    interest: '',
    range: '',
    message: '',
    accredited: false,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      setFormData((prev) => ({ ...prev, [name]: (e.target as HTMLInputElement).checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const honeypot = form.querySelector('[name="phone_alt"]') as HTMLInputElement;
    if (honeypot && honeypot.value.trim() !== '') {
      setSubmitted(true);
      return;
    }
    const formData = new FormData(form);
    const urlEncoded = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key !== 'phone_alt') {
        urlEncoded.append(key, value.toString());
      }
    });
    try {
      await fetch(form.action, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: urlEncoded.toString(),
      });
    } catch (_) {
      // proceed to success message regardless
    }
    setSubmitted(true);
  };

  const faqs = [
    { q: 'What is the minimum investment?', a: 'Our minimum commitment starts at €100,000 for accredited professional investors and €500,000 for institutional partners. Please contact our investor relations team to discuss eligibility.' },
    { q: 'How are returns distributed?', a: 'Returns vary by investment vehicle and structure. Generally, distributions are made quarterly or semi-annually based on fund performance and the specific terms of each investment mandate.' },
    { q: 'What due diligence do you provide?', a: 'Full transparency: quarterly performance reports, annual audits by leading firms, and access to underlying asset data through our secure investor portal. UK corporate governance ensures rigorous oversight.' },
    { q: 'Can non-UK investors participate?', a: 'Yes. We accept institutional and accredited professional investors from most jurisdictions, subject to local regulatory requirements and KYC/AML verification.' },
    { q: 'How do I get started?', a: 'Submit the inquiry form below or email our investor relations team directly. We typically respond within 24 hours to schedule an introductory conversation.' },
  ];

  return (
    <main>
      {/* Hero */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-20 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Modern%20bright%20Swiss%20corporate%20office%20interior%20with%20panoramic%20window%20overlooking%20alpine%20mountain%20landscape%2C%20Lake%20Thun%20crystal%20blue%20waters%20visible%2C%20elegant%20minimalist%20workspace%20with%20warm%20natural%20daylight%2C%20premium%20professional%20atmosphere%2C%20clean%20contemporary%20design%2C%20inviting%20warm%20tones%2C%20Swiss%20precision%20aesthetic%2C%20high%20end%20architectural%20interior%20photography&width=1920&height=700&seq=contact-hero-2026-v2&orientation=landscape"
            alt="CASA CHERE LIMITED – Contact Us"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-background-50" />
        </div>
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <span className="text-[10px] uppercase tracking-[3px] text-primary-300 font-semibold">
              Get in Touch
            </span>
            <h1 className="text-5xl md:text-7xl font-heading font-bold text-white mt-4 mb-6 tracking-tight">
              Contact Us
            </h1>
            <p className="text-lg text-white/80 leading-relaxed max-w-2xl">
              We welcome inquiries from institutional investors, family offices, sovereign wealth funds, and accredited professionals. Tell us about your investment interests and our team will respond within 24 hours.
            </p>
          </div>
        </div>
      </section>

      {/* Form + Sidebar */}
      <section className="relative py-16 md:py-24 bg-white">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-16">
            {/* Form */}
            <div className="lg:col-span-2">
              {submitted ? (
                <div className="bg-background-50 rounded-2xl p-12 text-center border border-foreground-100/20">
                  <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-6">
                    <i className="ri-check-line text-3xl text-primary-600" />
                  </div>
                  <h3 className="text-2xl font-heading font-bold text-foreground-950 mb-3">Thanks!</h3>
                  <p className="text-foreground-500 text-base max-w-md mx-auto">
                    Our team will contact you. Also you can use our email <a href="mailto:info@casachere.com" className="text-primary-600 hover:text-primary-700 underline cursor-pointer">info@casachere.com</a>
                  </p>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  action="https://readdy.ai/api/form/d8thjdhd9m2hsn3jsgtg"
                  method="POST"
                  data-readdy-form
                  className="bg-background-50 rounded-2xl p-8 md:p-10 border border-foreground-100/20"
                >
                  <h3 className="text-xl font-heading font-bold text-foreground-950 mb-2">
                    Investor Inquiry Form
                  </h3>
                  <p className="text-foreground-500 text-sm mb-8">
                    All fields marked with * are required.
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
                    <div>
                      <label className="block text-xs uppercase tracking-[2px] text-foreground-600 font-semibold mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-white border border-foreground-200/40 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="Your full name"
                      />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-[2px] text-foreground-600 font-semibold mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-white border border-foreground-200/40 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
                    <div>
                      <label className="block text-xs uppercase tracking-[2px] text-foreground-600 font-semibold mb-2">
                        Phone
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-white border border-foreground-200/40 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="+44 XX XXXX XXXX"
                      />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-[2px] text-foreground-600 font-semibold mb-2">
                        Organization
                      </label>
                      <input
                        type="text"
                        name="organization"
                        value={formData.organization}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-white border border-foreground-200/40 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="Company or institution"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
                    <div>
                      <label className="block text-xs uppercase tracking-[2px] text-foreground-600 font-semibold mb-2">
                        Investment Interest *
                      </label>
                      <select
                        name="interest"
                        required
                        value={formData.interest}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-white border border-foreground-200/40 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors cursor-pointer"
                      >
                        <option value="">Select...</option>
                        <option value="real-estate">Real Estate</option>
                        <option value="renewable-energy">Renewable Energy</option>
                        <option value="oil-gas">Oil &amp; Gas</option>
                        <option value="crypto">Crypto &amp; Digital Assets</option>
                        <option value="medtech">Medical Equipment</option>
                        <option value="multiple">Multiple Sectors</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-[2px] text-foreground-600 font-semibold mb-2">
                        Investment Range
                      </label>
                      <select
                        name="range"
                        value={formData.range}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-white border border-foreground-200/40 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors cursor-pointer"
                      >
                        <option value="">Select...</option>
                        <option value="100k-500k">€100K – €500K</option>
                        <option value="500k-2m">€500K – €2M</option>
                        <option value="2m-10m">€2M – €10M</option>
                        <option value="10m-plus">€10M+</option>
                      </select>
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block text-xs uppercase tracking-[2px] text-foreground-600 font-semibold mb-2">
                      Message
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      maxLength={500}
                      rows={5}
                      className="w-full px-4 py-3 bg-white border border-foreground-200/40 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                      placeholder="Tell us about your investment objectives and interests..."
                    />
                    <p className="text-foreground-300 text-xs mt-1.5">{formData.message.length}/500 characters</p>
                  </div>

                  <div className="mb-8">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        name="accredited"
                        required
                        checked={formData.accredited}
                        onChange={handleChange}
                        className="mt-0.5 w-4 h-4 rounded border-foreground-300 text-primary-500 focus:ring-primary-400 cursor-pointer"
                      />
                      <span className="text-foreground-500 text-sm leading-relaxed">
                        I confirm that I am an accredited or professional investor under applicable regulations and have read the disclaimer. *
                      </span>
                    </label>
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-primary-500 hover:bg-primary-600 text-white text-sm uppercase tracking-[2px] font-semibold py-4 rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
                  >
                    Submit Inquiry
                  </button>

                  <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" className="field-alt" />
                </form>
              )}
            </div>

            {/* Sidebar */}
            <div className="flex flex-col gap-6">
              {/* Contact Details */}
              <div className="bg-background-50 rounded-2xl p-6 md:p-8 border border-foreground-100/20">
                <h4 className="text-[10px] uppercase tracking-[3px] text-foreground-400 mb-5">
                  Headquarters
                </h4>
                <p className="text-foreground-700 text-sm leading-relaxed mb-4">
                  CASA CHERE LIMITED<br />
                  102 Fulham Palace Road<br />
                  London, England, W6 9PL<br />
                  United Kingdom
                </p>
                <div className="flex flex-col gap-2 text-sm">
                  <a href="mailto:info@casachere.com" className="text-primary-600 hover:text-primary-700 transition-colors cursor-pointer">
                    info@casachere.com
                  </a>
                </div>

                <div className="mt-6 pt-6 border-t border-foreground-100/20">
                  <h4 className="text-[10px] uppercase tracking-[3px] text-foreground-400 mb-3">
                    Registration
                  </h4>
                  <p className="text-foreground-500 text-xs">Company No. 08549152</p>
                  <p className="text-foreground-500 text-xs">SIC 68100 – Real Estate</p>
                </div>
              </div>

              {/* Why Partner */}
              <div className="bg-background-50 rounded-2xl p-6 md:p-8 border border-foreground-100/20">
                <h4 className="text-[10px] uppercase tracking-[3px] text-foreground-400 mb-5">
                  Why Partner With Us
                </h4>
                <div className="flex flex-col gap-4">
                  {[
                    { icon: 'ri-line-chart-line', label: '12.8% Avg Annual Return' },
                    { icon: 'ri-global-line', label: '18 Country Presence' },
                    { icon: 'ri-shield-check-line', label: 'UK Company Governance' },
                    { icon: 'ri-team-line', label: 'Dedicated IR Team' },
                  ].map((item) => (
                    <div key={item.label} className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center shrink-0">
                        <i className={`${item.icon} text-primary-600`} />
                      </div>
                      <span className="text-foreground-600 text-sm">{item.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map & Location */}
      <section className="relative py-20 md:py-28 bg-background-50">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <span className="text-[10px] uppercase tracking-[3px] text-primary-500 font-semibold">
              Find Us
            </span>
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-foreground-950 mt-4 mb-5 tracking-tight">
              Find Our Headquarters
            </h2>
            <p className="text-foreground-500 text-base max-w-2xl mx-auto">
              Our headquarters are located in Fulham, London, overlooking the River Thames in the heart of the United Kingdom.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Address Card */}
            <div className="bg-white rounded-2xl border border-foreground-100/20 p-8 flex flex-col justify-center">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center shrink-0">
                  <i className="ri-map-pin-line text-xl text-primary-600" />
                </div>
                <div>
                  <h3 className="text-sm font-heading font-bold text-foreground-950">Headquarters</h3>
                  <p className="text-foreground-400 text-xs">United Kingdom — London</p>
                </div>
              </div>

              <div className="flex flex-col gap-4">
                <div className="flex items-start gap-3">
                  <i className="ri-building-line text-foreground-400 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-foreground-700 text-sm leading-relaxed font-medium">CASA CHERE LIMITED</p>
                    <p className="text-foreground-500 text-sm leading-relaxed">102 Fulham Palace Road</p>
                    <p className="text-foreground-500 text-sm leading-relaxed">London, England, W6 9PL</p>
                    <p className="text-foreground-500 text-sm leading-relaxed">United Kingdom</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <i className="ri-mail-line text-foreground-400 mt-0.5 shrink-0" />
                  <a href="mailto:info@casachere.com" className="text-primary-600 hover:text-primary-700 text-sm transition-colors cursor-pointer">
                    info@casachere.com
                  </a>
                </div>

                <div className="flex items-start gap-3">
                  <i className="ri-car-line text-foreground-400 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-foreground-500 text-sm leading-relaxed">
                      5 minutes from Hammersmith Station<br />
                      20 minutes from Central London<br />
                      35 minutes from Heathrow Airport
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Map — takes 2 columns */}
            <div className="lg:col-span-2 bg-white rounded-2xl border border-foreground-100/20 overflow-hidden h-[400px] lg:h-auto min-h-[400px]">
              <iframe
                src="https://www.google.com/maps?q=102%20Fulham%20Palace%20Road%2C%20London%2C%20W6%209PL%2C%20United%20Kingdom&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="CASA CHERE LIMITED — London, United Kingdom"
                className="w-full h-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Leadership Team */}
      <section className="relative py-20 md:py-28 bg-background-50">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <span className="text-[10px] uppercase tracking-[3px] text-primary-500 font-semibold">
              Our Team
            </span>
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-foreground-950 mt-4 mb-5 tracking-tight">
              Leadership &amp; Key Executives
            </h2>
            <p className="text-foreground-500 text-base max-w-2xl mx-auto">
              Every member of our leadership team brings decades of specialized expertise — from institutional asset management to blockchain infrastructure, from UK regulatory compliance to global investor relations. Together, they execute the vision that has defined our firm since 2013.
            </p>
          </div>

          {/* Board Members — First */}
          <div className="max-w-5xl mx-auto mb-10">
            <div className="flex items-center gap-3 mb-8">
              <div className="h-px flex-1 bg-foreground-200/40" />
              <span className="text-[10px] uppercase tracking-[3px] text-primary-500 font-semibold px-4">Board &amp; Executive Leadership</span>
              <div className="h-px flex-1 bg-foreground-200/40" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto mb-16">
            {team.slice(0, 3).map((person) => (
              <div key={person.email} className="bg-white rounded-2xl p-7 border border-foreground-100/20 hover:border-primary-200/40 transition-all duration-300">
                <div className="shrink-0 mb-5">
                  {person.image ? (
                    <img
                      src={person.image}
                      alt={person.name}
                      className="w-24 h-24 rounded-xl object-cover"
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-xl bg-background-100 border border-foreground-100/30 flex items-center justify-center">
                      <span className="text-xl font-heading font-bold text-foreground-300">
                        {(() => { const parts = person.name.replace('Dr. ', '').split(' '); return parts[0][0] + parts[parts.length - 1][0]; })()}
                      </span>
                    </div>
                  )}
                </div>
                <h3 className="text-base font-heading font-bold text-foreground-950">{person.name}</h3>
                <p className="text-primary-500 text-[10px] uppercase tracking-[1.5px] font-semibold mt-1 mb-3">{person.role}</p>
                <p className="text-foreground-500 text-xs leading-relaxed mb-4">{person.impact}</p>
                <a href={`mailto:${person.email}`} className="inline-flex items-center gap-1.5 text-primary-500 hover:text-primary-600 text-xs transition-colors cursor-pointer">
                  <i className="ri-mail-line" />
                  {person.email}
                </a>
              </div>
            ))}
          </div>

          {/* Executive Team — Remaining */}
          <div className="max-w-5xl mx-auto mb-10">
            <div className="flex items-center gap-3 mb-8">
              <div className="h-px flex-1 bg-foreground-200/40" />
              <span className="text-[10px] uppercase tracking-[3px] text-primary-500 font-semibold px-4">Investment &amp; Operations Team</span>
              <div className="h-px flex-1 bg-foreground-200/40" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {team.slice(3).map((person) => (
              <div key={person.email} className="bg-white rounded-2xl p-6 border border-foreground-100/20 hover:border-primary-200/40 transition-all duration-300">
                {person.image ? (
                  <img
                    src={person.image}
                    alt={person.name}
                    className="w-20 h-20 rounded-xl object-cover mb-4"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-xl bg-background-100 border border-foreground-100/30 flex items-center justify-center mb-4">
                    <span className="text-lg font-heading font-bold text-foreground-300">
                      {(() => { const parts = person.name.replace('Dr. ', '').split(' '); return parts[0][0] + parts[parts.length - 1][0]; })()}
                    </span>
                  </div>
                )}
                <h3 className="text-sm font-heading font-bold text-foreground-950">{person.name}</h3>
                <p className="text-primary-500 text-[10px] uppercase tracking-[1.5px] font-semibold mt-1 mb-3">{person.role}</p>
                <p className="text-foreground-500 text-xs leading-relaxed mb-3">{person.impact}</p>
                <a href={`mailto:${person.email}`} className="inline-flex items-center gap-1.5 text-primary-500 hover:text-primary-600 text-xs transition-colors cursor-pointer">
                  <i className="ri-mail-line" />
                  {person.email}
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative py-20 md:py-28 bg-white">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <span className="text-[10px] uppercase tracking-[3px] text-primary-500 font-semibold">
              FAQ
            </span>
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-foreground-950 mt-4 tracking-tight">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="max-w-3xl mx-auto flex flex-col gap-3">
            {faqs.map((faq) => (
              <details key={faq.q} className="group bg-background-50 rounded-xl border border-foreground-100/20">
                <summary className="flex items-center justify-between px-6 py-5 cursor-pointer list-none">
                  <span className="text-foreground-900 font-medium text-sm pr-4">{faq.q}</span>
                  <i className="ri-add-line text-foreground-400 group-open:hidden shrink-0" />
                  <i className="ri-subtract-line text-foreground-400 hidden group-open:block shrink-0" />
                </summary>
                <div className="px-6 pb-5">
                  <p className="text-foreground-500 text-sm leading-relaxed">{faq.a}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}